<template>
  <div class="contract-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>合同管理</span>
          <div>
            <el-button type="success" @click="handleExport" :loading="exportLoading">导出合同</el-button>
            <el-button type="primary" @click="handleCreate">新增合同</el-button>
          </div>
        </div>
      </template>

      <!-- 搜索表单 -->
      <el-form :model="queryParams" class="search-form" label-width="80px">
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item label="客户">
              <el-input
                v-model="queryParams.customerName"
                placeholder="请输入客户姓名"
                clearable
                style="width: 100%"
                @keyup.enter="handleSearch"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="合同状态">
              <el-select v-model="queryParams.contractStatus" placeholder="全部" clearable style="width: 100%">
                <el-option
                  v-for="item in statusOptions"
                  :key="item.code"
                  :label="item.name"
                  :value="item.code"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="合同类型">
              <el-select v-model="queryParams.contractType" placeholder="全部" clearable style="width: 100%">
                <el-option
                  v-for="item in contractTypeOptions"
                  :key="item.code"
                  :label="item.name"
                  :value="item.code"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="合同备注">
              <el-input
                v-model="queryParams.remark"
                placeholder="请输入合同备注"
                clearable
                style="width: 100%"
                @keyup.enter="handleSearch"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item label="创建时间">
              <el-date-picker
                v-model="createTimeRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label=" ">
              <div class="action-buttons-inline">
                <el-button type="primary" @click="handleSearch">查询</el-button>
                <el-button @click="handleReset">重置</el-button>
              </div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <!-- 合同列表 -->
      <el-table 
        v-loading="loading" 
        :data="contractList" 
        border 
        stripe 
        style="width: 100%"
      >
        <el-table-column prop="contractId" label="ID" width="80" />
        <el-table-column prop="contractNumber" label="合同编号" width="180" show-overflow-tooltip />
        <el-table-column prop="customerName" label="客户姓名" width="150" show-overflow-tooltip />
        <el-table-column prop="contractType" label="合同类型" width="120">
          <template #default="{ row }">
            {{ getContractTypeName(row.contractType) }}
          </template>
        </el-table-column>
        <el-table-column prop="contractAmount" label="合同金额" width="130" align="right">
          <template #default="{ row }">
            <span style="color: #f56c6c; font-weight: bold">¥{{ formatMoney(row.contractAmount || 0) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="startDate" label="开始日期" width="120" align="center">
          <template #default="{ row }">
            {{ row.startDate || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="endDate" label="结束日期" width="120" align="center">
          <template #default="{ row }">
            {{ row.endDate || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="合同备注" min-width="150" show-overflow-tooltip>
          <template #default="{ row }">
            {{ row.remark || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="contractStatus" label="状态" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.contractStatus)" size="small">
              {{ getStatusName(row.contractStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="160">
          <template #default="{ row }">
            {{ formatDateTime(row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" fixed="right" class-name="operation-column">
          <template #default="{ row }">
            <div class="operation-buttons">
              <el-button type="primary" size="small" @click="handleView(row)">详情</el-button>
              <el-button type="warning" size="small" @click="handleEdit(row)" v-if="row.contractStatus === 'draft'">编辑</el-button>
              <el-tooltip content="修正并重新发起审批" placement="top">
                <el-button
                  type="success"
                  size="small"
                  @click="handleCorrect(row)"
                  v-if="row.contractStatus === 'rejected' || row.contractStatus === 'REJECTED'"
                >
                  修正
                </el-button>
              </el-tooltip>
              <el-button type="danger" size="small" @click="handleWithdraw(row)" v-if="row.contractStatus === 'pending'">撤销</el-button>
              <el-button type="info" size="small" @click="handleViewApprovalProgress(row)" v-if="row.contractStatus !== 'draft'">审批进度</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.page"
          v-model:page-size="queryParams.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 合同详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="合同详情"
      width="1400px"
      @close="handleDetailDialogClose"
    >
      <div v-loading="detailLoading" class="contract-detail-container">
        <!-- 左侧：合同详情 -->
        <div class="contract-detail-left" :class="{ 'full-width': contractDetail.contractStatus === 'draft' }">
          <div v-loading="detailLoading" class="contract-detail">
        <!-- 基础信息卡片 -->
        <el-card class="detail-card" shadow="never">
          <template #header>
            <div class="card-title">
              <span class="title-text">基础信息</span>
              <span class="title-tip">合同基础资料</span>
            </div>
          </template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="合同编号">
              {{ contractDetail.contractNumber || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="客户姓名">
              {{ contractDetail.customerName || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="合同类型">
              <el-tag :type="contractDetail.contractType === 'deal' ? 'success' : 'warning'" size="small">
                {{ getContractTypeName(contractDetail.contractType) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="合同状态">
              <el-tag :type="getStatusType(contractDetail.contractStatus)" size="small">
                {{ getStatusName(contractDetail.contractStatus) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="合同金额">
              <span style="color: #f56c6c; font-weight: bold">¥{{ formatMoney(contractDetail.contractAmount || 0) }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="开始日期">
              {{ contractDetail.startDate || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="结束日期">
              {{ contractDetail.endDate || '-' }}
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- 备注卡片 -->
        <el-card class="detail-card" shadow="never">
          <template #header>
            <div class="card-title">
              <span class="title-text">备注</span>
            </div>
          </template>
          <el-descriptions :column="1" border>
            <el-descriptions-item label="备注">
              <div style="white-space: pre-wrap; word-break: break-word;">
                {{ contractDetail.remark || '-' }}
              </div>
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- 合同图片卡片 -->
        <el-card class="detail-card" shadow="never" v-if="contractDetail.contractImages && contractDetail.contractImages.length > 0">
          <template #header>
            <div class="card-title">
              <span class="title-text">合同图片</span>
            </div>
          </template>
          <ImagePreview :model-value="contractDetail.contractImages" />
        </el-card>

        <!-- 合同附件卡片 -->
        <el-card class="detail-card" shadow="never" v-if="contractDetail.contractAttachments && contractDetail.contractAttachments.length > 0">
          <template #header>
            <div class="card-title">
              <span class="title-text">合同附件</span>
            </div>
          </template>
          <div class="attachment-list">
            <!-- 图片附件：显示缩略图 -->
            <div
              v-for="(attachment, index) in contractDetail.contractAttachments"
              :key="index"
              :class="['attachment-item', { 'attachment-item-image': isAttachmentImage(attachment) }]"
            >
              <template v-if="isAttachmentImage(attachment)">
                <div class="attachment-image-wrapper">
                  <el-image
                    :src="getAttachmentUrl(attachment)"
                    :alt="getAttachmentFileName(attachment)"
                    :preview-src-list="getImagePreviewList()"
                    :initial-index="getImageIndex(index)"
                    fit="cover"
                    class="attachment-image-preview"
                    :preview-teleported="true"
                    crossorigin="anonymous"
                    referrerpolicy="no-referrer-when-downgrade"
                    @error="handleImageError"
                  >
                    <template #error>
                      <div class="image-slot">
                        <el-icon><Picture /></el-icon>
                      </div>
                    </template>
                  </el-image>
                  <!-- 下载按钮覆盖层 -->
                  <div class="download-overlay">
                    <el-button
                      type="primary"
                      :icon="Download"
                      circle
                      size="small"
                      class="download-overlay-btn"
                      @click.stop="handleDownloadAttachment(attachment)"
                      title="下载图片"
                    />
                  </div>
                </div>
                <div class="attachment-image-info">
                  <div class="attachment-image-details">
                    <span class="attachment-name" :title="getAttachmentFileName(attachment)">
                      {{ getAttachmentFileName(attachment) }}
                    </span>
                    <span v-if="getAttachmentSize(attachment) > 0" class="attachment-size">
                      ({{ formatFileSize(getAttachmentSize(attachment)) }})
                    </span>
                  </div>
                </div>
              </template>
              
              <!-- 非图片附件：显示图标 -->
              <template v-else>
                <div :class="['attachment-icon-wrapper', getFileIconClass(attachment)]">
                  <el-icon class="attachment-icon">
                    <component :is="getFileIcon(attachment)" />
                  </el-icon>
                  <span v-if="getFileTypeLabel(attachment)" class="file-type-label">
                    {{ getFileTypeLabel(attachment) }}
                  </span>
                </div>
                <span class="attachment-name" :title="getAttachmentFileName(attachment)">
                  {{ getAttachmentFileName(attachment) }}
                </span>
                <span class="attachment-size" v-if="getAttachmentSize(attachment) > 0">
                  ({{ formatFileSize(getAttachmentSize(attachment)) }})
                </span>
                <div class="attachment-actions">
                  <el-button
                    type="primary"
                    size="small"
                    :icon="Download"
                    link
                    @click="handleDownloadAttachment(attachment)"
                  >
                    下载
                  </el-button>
                </div>
              </template>
            </div>
          </div>
        </el-card>

        <!-- 审批信息卡片 -->
        <el-card class="detail-card" shadow="never">
          <template #header>
            <div class="card-title">
              <span class="title-text">审批信息</span>
            </div>
          </template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="起草人">
              {{ contractDetail.draftBy || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="起草时间">
              {{ formatDateTime(contractDetail.draftTime) }}
            </el-descriptions-item>
            <el-descriptions-item label="审核人">
              {{ contractDetail.reviewBy || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="审核时间">
              {{ formatDateTime(contractDetail.reviewTime) }}
            </el-descriptions-item>
            <el-descriptions-item label="创建人">
              {{ contractDetail.createBy || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="创建时间">
              {{ formatDateTime(contractDetail.createTime) }}
            </el-descriptions-item>
          </el-descriptions>
        </el-card>
          </div>
        </div>
        
        <!-- 右侧：审批记录 -->
        <div class="contract-detail-right" v-if="contractDetail.contractStatus !== 'draft'">
          <el-card class="approval-record-card" shadow="never">
            <template #header>
              <div class="card-title">
                <span class="title-text">审批记录</span>
              </div>
            </template>
            <div v-loading="approvalRecordLoading">
              <ApprovalTimeline :records="approvalRecords" />
            </div>
          </el-card>
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <!-- 待审批状态：显示撤销按钮 -->
          <div v-if="contractDetail.contractStatus === 'pending'" class="footer-center">
            <el-button type="danger" size="large" style="width: 200px" @click="handleWithdrawInDetail">
              撤销
            </el-button>
          </div>
          <!-- 草稿状态：显示编辑和提交审批按钮 -->
          <div v-else-if="contractDetail.contractStatus === 'draft'" class="footer-buttons">
            <el-button @click="handleEditInDetail">编辑</el-button>
            <div style="position: relative; display: inline-block;">
              <div v-if="isPersonalUser" style="position: absolute; top: -20px; right: 0; font-size: 12px; color: #67c23a; white-space: nowrap;">
                个人用户点提交审批直接通过
              </div>
              <el-button class="submit-approval-btn" @click="handleSubmitApproval">提交审批</el-button>
            </div>
          </div>
          <!-- 已驳回状态：显示修正按钮 -->
          <div
            v-else-if="contractDetail.contractStatus === 'rejected' || contractDetail.contractStatus === 'REJECTED'"
            class="footer-center"
          >
            <el-tooltip content="修正并重新发起审批" placement="top">
              <el-button type="success" size="large" style="width: 200px" @click="handleCorrectInDetail">
                修正
              </el-button>
            </el-tooltip>
          </div>
          <!-- 其他状态：只显示关闭按钮 -->
          <div v-else class="footer-right">
            <el-button @click="detailDialogVisible = false">关闭</el-button>
          </div>
        </div>
      </template>
    </el-dialog>

    <!-- 审批进度对话框 -->
    <el-dialog
      v-model="approvalProgressDialogVisible"
      title="审批进度"
      width="600px"
      @close="approvalProgressDialogVisible = false"
    >
      <div v-loading="approvalProgressLoading" class="approval-progress-dialog">
        <div class="contract-info" v-if="approvalProgressContract.contractNumber">
          <el-descriptions :column="2" border>
            <el-descriptions-item label="合同编号">
              {{ approvalProgressContract.contractNumber }}
            </el-descriptions-item>
            <el-descriptions-item label="客户姓名">
              {{ approvalProgressContract.customerName }}
            </el-descriptions-item>
          </el-descriptions>
        </div>
        <div class="approval-timeline-wrapper">
          <ApprovalTimeline :records="approvalProgressRecords" />
        </div>
      </div>
      <template #footer>
        <el-button @click="approvalProgressDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onActivated, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Document, Download, Picture, VideoPlay } from '@element-plus/icons-vue'
import ApprovalTimeline from '../components/ApprovalTimeline.vue'
import ImagePreview from '../components/ImagePreview.vue'
import { formatFileSize, isImageFile, downloadFileFromServer } from '../utils/fileUtils'
import { getPreviewUrl } from '../api/file'
import {
  getContractList,
  getContractDetail,
  withdrawContract,
  updateContract,
  getApprovalRecords,
  exportContractList
} from '../api/salesman'
import { checkQuotaWithLocalFallback, QUOTA_TYPE } from '../utils/quotaCheck'

// 获取企业ID（用于判断是否是个人用户）
const getCompanyId = () => {
  try {
    const userInfoStr = localStorage.getItem('userInfo')
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr)
      return userInfo.companyId || null
    }
  } catch (e) {
    console.warn('解析用户信息失败', e)
  }
  return null
}

// 判断是否是个人用户（没有企业ID）
const isPersonalUser = computed(() => {
  return !getCompanyId()
})

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const contractList = ref([])
const total = ref(0)
const exportLoading = ref(false)
// 上次导出时间，用于限流
const lastExportTime = ref(0)
// 导出限流间隔时间（毫秒），3秒内不允许重复导出
const EXPORT_THROTTLE_INTERVAL = 3000
const queryParams = reactive({
  customerId: null,
  customerName: '',
  contractStatus: '',
  contractType: '',
  remark: '',
  startDate: '',
  endDate: '',
  page: 1,
  pageSize: 10
})

// 合同详情相关
const detailDialogVisible = ref(false)
const detailLoading = ref(false)
const contractDetail = reactive({
  contractId: null,
  contractNumber: '',
  customerName: '',
  contractType: '',
  contractStatus: '',
  contractAmount: null,
  startDate: '',
  endDate: '',
  content: '',
  remark: '',
  contractImages: [],
  contractAttachments: [],
  draftBy: '',
  draftTime: null,
  reviewBy: '',
  reviewTime: null,
  createBy: '',
  createTime: null
})

// 审批记录相关
const approvalRecords = ref([])
const approvalRecordLoading = ref(false)

// 审批进度对话框
const approvalProgressDialogVisible = ref(false)
const approvalProgressLoading = ref(false)
const approvalProgressRecords = ref([])
const approvalProgressContract = reactive({
  contractId: null,
  contractNumber: '',
  customerName: ''
})

// 创建时间范围（用于日期选择器）
const createTimeRange = ref([])

// 状态选项（移除已撤销）
const statusOptions = ref([
  { code: 'draft', name: '草稿' },
  { code: 'pending', name: '待审批' },
  { code: 'approving', name: '审批中' },
  { code: 'approved', name: '已通过' },
  { code: 'rejected', name: '已驳回' }
])

// 合同类型选项
const contractTypeOptions = ref([
  { code: 'intention', name: '意向合同' },
  { code: 'deal', name: '成交合同' }
])

const getStatusName = (status) => {
  if (!status) return '-'
  const option = statusOptions.value.find(item => item.code === status)
  // 兼容处理：如果状态不在选项中（如已撤销），返回状态码本身或默认值
  if (option) {
    return option.name
  }
  // 兼容已移除的"已撤销"状态
  if (status === 'cancelled') {
    return '已撤销'
  }
  return status
}

const getStatusType = (status) => {
  const map = {
    draft: 'info',
    pending: 'warning',
    approving: 'warning',
    approved: 'success',
    rejected: 'danger',
    cancelled: 'info' // 兼容已撤销状态显示
  }
  return map[status] || 'info'
}

const getContractTypeName = (type) => {
  if (!type) return '-'
  const map = {
    intention: '意向合同',
    deal: '成交合同',
    formal: '正式合同',
    framework: '框架合同'
  }
  // 支持大小写不敏感匹配
  const lowerType = type.toLowerCase()
  return map[lowerType] || map[type] || type
}

const formatMoney = (amount) => {
  if (!amount) return '0.00'
  return Number(amount).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const formatDateTime = (dateTime) => {
  if (!dateTime) return '-'
  return new Date(dateTime).toLocaleString('zh-CN')
}

const loadContractList = async () => {
  loading.value = true
  try {
    // 构建查询参数
    const params = {
      page: queryParams.page,
      pageSize: queryParams.pageSize
    }
    if (queryParams.customerName) {
      params.customerName = queryParams.customerName
    }
    if (queryParams.contractStatus) {
      params.contractStatus = queryParams.contractStatus
    }
    if (queryParams.contractType) {
      params.contractType = queryParams.contractType
    }
    if (queryParams.remark) {
      params.remark = queryParams.remark
    }
    if (queryParams.startDate) {
      params.startDate = queryParams.startDate
    }
    if (queryParams.endDate) {
      params.endDate = queryParams.endDate
    }
    
    const res = await getContractList(params)
    if (res.code === '000000') {
      // 处理分页数据：后端返回 { list: [], total: 0, page: 1, pageSize: 10 } 格式
      contractList.value = res.data?.list || []
      total.value = res.data?.total || 0
    } else {
      ElMessage.error(res.message || '获取合同列表失败')
    }
  } catch (error) {
    console.error('获取合同列表失败:', error)
    ElMessage.error('获取合同列表失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  // 处理创建时间范围
  if (createTimeRange.value && createTimeRange.value.length === 2) {
    queryParams.startDate = createTimeRange.value[0]
    queryParams.endDate = createTimeRange.value[1]
  } else {
    queryParams.startDate = ''
    queryParams.endDate = ''
  }
  
  queryParams.page = 1
  loadContractList()
}

const handleReset = () => {
  queryParams.customerId = null
  queryParams.customerName = ''
  queryParams.contractStatus = ''
  queryParams.contractType = ''
  queryParams.remark = ''
  queryParams.startDate = ''
  queryParams.endDate = ''
  queryParams.page = 1
  createTimeRange.value = []
  loadContractList()
}

const handleCreate = async () => {
  // 先检查合同额度
  const result = await checkQuotaWithLocalFallback(QUOTA_TYPE.CONTRACT, {
    allowLocalOperation: false,
    operationName: '新增合同'
  })
  
  if (!result.allowed) {
    // 额度已超限，不允许新增
    return
  }
  
  router.push('/home/salesman-contract-form')
}

// 处理审批记录数据，适配时间轴组件
const processApprovalRecords = (records) => {
  if (!Array.isArray(records) || records.length === 0) {
    return []
  }

  // 不再过滤 CANCELLED/CANCELED，确保驳回后续节点也可展示审批人
  // 按节点顺序排序（nodeOrder 或 currentNodeIndex），如果没有则按创建时间正序排列
  const sortedRecords = [...records].sort((a, b) => {
    // 优先按 nodeOrder 排序
    if (a.nodeOrder !== undefined && b.nodeOrder !== undefined) {
      return a.nodeOrder - b.nodeOrder
    }
    // 其次按 currentNodeIndex 排序
    if (a.currentNodeIndex !== undefined && b.currentNodeIndex !== undefined) {
      return a.currentNodeIndex - b.currentNodeIndex
    }
    // 最后按创建时间正序排列（最早的在前）
    const timeA = a.createTime ? new Date(a.createTime).getTime() : 0
    const timeB = b.createTime ? new Date(b.createTime).getTime() : 0
    return timeA - timeB
  })
  
  // 检查是否有节点被拒绝
  const rejectedIndex = sortedRecords.findIndex(r => {
    const status = String(r.approvalStatus || '').toUpperCase().trim()
    return status === 'REJECTED'
  })
  const hasRejected = rejectedIndex !== -1
  
  return sortedRecords.map((record, index) => {
    let finalStatus = record.approvalStatus
    if (hasRejected && index > rejectedIndex) {
      finalStatus = 'CANCELLED'
    } else if (!finalStatus || String(finalStatus).trim() === '') {
      finalStatus = 'PENDING'
    }
    const normalizedStatus = finalStatus ? String(finalStatus).toUpperCase().trim() : 'PENDING'

    return {
      ...record,
      currentNode: record.currentNode || record.nodeName || '审批节点',
      approverName: record.approver || record.approverName || '-',
      statusName: getApprovalStatusName(normalizedStatus),
      createTimeFormatted: record.createTime ? formatDateTime(record.createTime) : '',
      approvalStatus: normalizedStatus
    }
  })
}

// 获取审批状态名称
const getApprovalStatusName = (status) => {
  if (!status) return '待审批'
  const statusUpper = String(status).toUpperCase().trim()
  const statusMap = {
    'PENDING': '待审批',
    'APPROVING': '审批中',
    'APPROVED': '已通过',
    'REJECTED': '已驳回',
    'WAITING': '等待中',
    'CANCELLED': '已取消',
    'CANCELED': '已取消'
  }
  return statusMap[statusUpper] || '待审批'
}

// 加载审批记录
const loadApprovalRecords = async (contractId) => {
  if (!contractId) {
    approvalRecords.value = []
    return
  }
  approvalRecordLoading.value = true
  try {
    const res = await getApprovalRecords('CONTRACT', contractId)
    if (res.code === '000000') {
      approvalRecords.value = processApprovalRecords(res.data || [])
    } else {
      console.error('获取审批记录失败:', res.message)
      approvalRecords.value = []
    }
  } catch (error) {
    console.error('获取审批记录失败:', error)
    approvalRecords.value = []
  } finally {
    approvalRecordLoading.value = false
  }
}

const handleView = async (row) => {
  detailLoading.value = true
  detailDialogVisible.value = true
  try {
    // 使用 contractId 而不是 id
    const contractId = row.contractId || row.id
    if (!contractId) {
      ElMessage.error('合同ID不存在')
      detailDialogVisible.value = false
      return
    }
    const res = await getContractDetail(contractId)
    if (res.code === '000000') {
      // 清空旧数据
      Object.keys(contractDetail).forEach(key => {
        if (Array.isArray(contractDetail[key])) {
          contractDetail[key] = []
        } else {
          contractDetail[key] = null
        }
      })
      // 填充新数据
      Object.assign(contractDetail, res.data)
      contractDetail.contractId = res.data.contractId
      // 确保数组字段正确初始化
      if (!contractDetail.contractImages) {
        contractDetail.contractImages = []
      }
      if (!contractDetail.contractAttachments) {
        contractDetail.contractAttachments = []
      }
      
      // 处理附件数据，从后端获取文件名和大小
      if (contractDetail.contractAttachments && contractDetail.contractAttachments.length > 0) {
        const { getFileName } = await import('../api/file')
        contractDetail.contractAttachments = await Promise.all(contractDetail.contractAttachments.map(async (att) => {
          if (typeof att === 'string') {
            // 如果是URL字符串，从后端获取文件名
            let fileName = att.split('/').pop() || '附件'
            try {
              const fileNameRes = await getFileName(att)
              if (fileNameRes.code === '000000') {
                // 优先使用 data 字段，如果没有则使用 msg 字段
                fileName = fileNameRes.data || fileNameRes.msg || fileName
              }
            } catch (error) {
              console.warn('获取文件名失败:', error)
            }
            return {
              url: att,
              fileName: fileName,
              originalFileName: fileName,
              size: 0
            }
          }
          // 如果已经是对象，确保有 originalFileName
          if (att.originalFileName) {
            return att
          }
          return {
            ...att,
            originalFileName: att.fileName || att.name || '附件'
          }
        }))
      }
      
      // 如果不是草稿状态，加载审批记录
      if (contractDetail.contractStatus !== 'draft') {
        await loadApprovalRecords(contractId)
      } else {
        approvalRecords.value = []
      }
    } else {
      ElMessage.error(res.message || '获取合同详情失败')
      detailDialogVisible.value = false
    }
  } catch (error) {
    console.error('获取合同详情失败:', error)
    ElMessage.error('获取合同详情失败')
    detailDialogVisible.value = false
  } finally {
    detailLoading.value = false
  }
}

// 查看审批进度
const handleViewApprovalProgress = async (row) => {
  approvalProgressLoading.value = true
  approvalProgressDialogVisible.value = true
  try {
    const contractId = row.contractId || row.id
    if (!contractId) {
      ElMessage.error('合同ID不存在')
      approvalProgressDialogVisible.value = false
      return
    }
    // 设置合同基本信息
    approvalProgressContract.contractId = contractId
    approvalProgressContract.contractNumber = row.contractNumber || ''
    approvalProgressContract.customerName = row.customerName || ''
    // 加载审批记录
    const res = await getApprovalRecords('CONTRACT', contractId)
    if (res.code === '000000') {
      approvalProgressRecords.value = processApprovalRecords(res.data || [])
    } else {
      ElMessage.error(res.message || '获取审批记录失败')
      approvalProgressRecords.value = []
    }
  } catch (error) {
    console.error('获取审批记录失败:', error)
    ElMessage.error('获取审批记录失败')
    approvalProgressRecords.value = []
  } finally {
    approvalProgressLoading.value = false
  }
}

const handleDetailDialogClose = () => {
  // 清空详情数据
  Object.keys(contractDetail).forEach(key => {
    if (Array.isArray(contractDetail[key])) {
      contractDetail[key] = []
    } else {
      contractDetail[key] = null
    }
  })
  // 清空审批记录
  approvalRecords.value = []
}

// 获取附件文件名（优先显示原始文件名）
const getAttachmentFileName = (attachment) => {
  if (!attachment) return '未知文件'
  // 如果是对象，优先使用原始文件名
  if (typeof attachment === 'object') {
    if (attachment.originalFileName) {
      return attachment.originalFileName
    }
    if (attachment.fileName) {
      return attachment.fileName
    }
    if (attachment.name) {
      return attachment.name
    }
    if (attachment.url) {
      const parts = attachment.url.split('/')
      const fileName = parts[parts.length - 1].split('?')[0]
      return fileName || '附件'
    }
  }
  // 如果是字符串URL
  if (typeof attachment === 'string') {
    const parts = attachment.split('/')
    const fileName = parts[parts.length - 1].split('?')[0]
    return fileName || '附件'
  }
  return '未知文件'
}

// 获取附件大小
const getAttachmentSize = (attachment) => {
  if (!attachment) return 0
  if (typeof attachment === 'object' && attachment.size) {
    return attachment.size
  }
  return 0
}

// 获取文件扩展名
const getFileExtension = (attachment) => {
  const fileName = getAttachmentFileName(attachment)
  if (!fileName) return ''
  const parts = fileName.split('.')
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
}

// 获取文件类型图标
const getFileIcon = (attachment) => {
  const ext = getFileExtension(attachment)
  // 图片类型
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
    return Picture
  }
  // 视频类型
  if (['mp4', 'avi', 'mov', 'wmv', 'flv'].includes(ext)) {
    return VideoPlay
  }
  // 其他类型使用文档图标
  return Document
}

// 获取文件类型标签（显示在图标上）
const getFileTypeLabel = (attachment) => {
  const ext = getFileExtension(attachment)
  const labelMap = {
    'xls': 'XLS',
    'xlsx': 'XLSX',
    'doc': 'DOC',
    'docx': 'DOCX',
    'pdf': 'PDF',
    'txt': 'TXT',
    'ppt': 'PPT',
    'pptx': 'PPTX',
    'zip': 'ZIP',
    'rar': 'RAR',
    '7z': '7Z'
  }
  return labelMap[ext] || ''
}

// 获取文件类型图标样式类
const getFileIconClass = (attachment) => {
  const ext = getFileExtension(attachment)
  const iconClassMap = {
    // Excel
    'xls': 'icon-excel',
    'xlsx': 'icon-excel',
    // Word
    'doc': 'icon-word',
    'docx': 'icon-word',
    // PDF
    'pdf': 'icon-pdf',
    // 文本
    'txt': 'icon-txt',
    // 图片
    'jpg': 'icon-image',
    'jpeg': 'icon-image',
    'png': 'icon-image',
    'gif': 'icon-image',
    'bmp': 'icon-image',
    'webp': 'icon-image',
    // PPT
    'ppt': 'icon-ppt',
    'pptx': 'icon-ppt',
    // 压缩包
    'zip': 'icon-zip',
    'rar': 'icon-zip',
    '7z': 'icon-zip'
  }
  return iconClassMap[ext] || 'icon-default'
}

// 判断附件是否为图片
const isAttachmentImage = (attachment) => {
  if (!attachment) return false
  const fileName = getAttachmentFileName(attachment)
  return isImageFile(fileName)
}

// 获取附件URL
const getAttachmentUrl = (attachment) => {
  let url = ''
  if (typeof attachment === 'string') {
    url = attachment
  } else if (attachment && attachment.url) {
    url = attachment.url
  } else {
    return ''
  }
  
  // 如果是blob URL，直接返回（不需要代理）
  if (url.startsWith('blob:')) {
    return url
  }
  
  // 对于COS URL，使用代理接口（解决403和CORS问题）
  return getPreviewUrl(url)
}

// 获取所有图片的预览列表（用于 el-image 的预览功能）
const getImagePreviewList = () => {
  if (!contractDetail.contractAttachments) return []
  return contractDetail.contractAttachments
    .filter(att => isAttachmentImage(att) && getAttachmentUrl(att))
    .map(att => getAttachmentUrl(att))
}

// 获取当前图片在预览列表中的索引
const getImageIndex = (currentIndex) => {
  let imageIndex = 0
  for (let i = 0; i < currentIndex; i++) {
    const att = contractDetail.contractAttachments[i]
    if (att && isAttachmentImage(att) && getAttachmentUrl(att)) {
      imageIndex++
    }
  }
  return imageIndex
}

// 处理图片加载错误
const handleImageError = (event) => {
  if (event.target) {
    event.target.style.display = 'none'
  }
}

// 下载附件
const handleDownloadAttachment = async (attachment) => {
  let url = typeof attachment === 'string' ? attachment : attachment.url
  let fileName = typeof attachment === 'string' ? undefined : (attachment.originalFileName || attachment.fileName)
  
  if (!url) {
    ElMessage.warning('附件地址无效')
    return
  }
  
  try {
    await downloadFileFromServer(url, fileName)
  } catch (error) {
    console.error('下载附件失败:', error)
    ElMessage.error(error.message || '下载附件失败')
  }
}

const handleEdit = (row) => {
  const contractId = row.contractId || row.id
  if (!contractId) {
    ElMessage.error('合同ID不存在')
    return
  }
  router.push({
    path: '/home/salesman-contract-form',
    query: { id: contractId }
  })
}

// 修正功能（从列表页）
const handleCorrect = (row) => {
  const contractId = row.contractId || row.id
  if (!contractId) {
    ElMessage.warning('数据异常，无法修正')
    return
  }
  const status = (row.contractStatus || '').toLowerCase()
  if (status !== 'rejected') {
    ElMessage.warning('只有已驳回状态的合同可以修正')
    return
  }
  router.push({
    path: '/home/salesman-contract-form',
    query: { correctId: contractId, mode: 'correct' }
  })
}

// 修正功能（从详情页）
const handleCorrectInDetail = () => {
  if (!contractDetail.value || !contractDetail.value.contractId) {
    ElMessage.warning('数据异常，无法修正')
    return
  }
  const status = (contractDetail.value.contractStatus || '').toLowerCase()
  if (status !== 'rejected') {
    ElMessage.warning('只有已驳回状态的合同可以修正')
    return
  }
  detailDialogVisible.value = false
  router.push({
    path: '/home/salesman-contract-form',
    query: { correctId: contractDetail.value.contractId, mode: 'correct' }
  })
}

// 重新加载详情数据
const reloadContractDetail = async () => {
  const contractId = contractDetail.contractId
  if (!contractId) {
    return
  }
  detailLoading.value = true
  try {
    const res = await getContractDetail(contractId)
    if (res.code === '000000') {
      // 清空旧数据
      Object.keys(contractDetail).forEach(key => {
        if (Array.isArray(contractDetail[key])) {
          contractDetail[key] = []
        } else {
          contractDetail[key] = null
        }
      })
      // 填充新数据
      Object.assign(contractDetail, res.data)
      contractDetail.contractId = res.data.contractId
      // 确保数组字段正确初始化
      if (!contractDetail.contractImages) {
        contractDetail.contractImages = []
      }
      if (!contractDetail.contractAttachments) {
        contractDetail.contractAttachments = []
      }
      
      // 处理附件数据，优先使用 contractAttachmentInfos（包含原始文件名和大小）
      if (res.data.contractAttachmentInfos && Array.isArray(res.data.contractAttachmentInfos) && res.data.contractAttachmentInfos.length > 0) {
        // 使用后端返回的附件详细信息
        contractDetail.contractAttachments = res.data.contractAttachmentInfos.map(att => {
          let url = att.url
          if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
            const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
            url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
          }
          return {
            url: url || att.url,
            fileName: att.fileName || att.originalFileName || '附件',
            originalFileName: att.originalFileName || att.fileName || '附件',
            size: att.size || 0
          }
        })
      } else if (contractDetail.contractAttachments && contractDetail.contractAttachments.length > 0) {
        // 兼容旧格式：如果 contractAttachmentInfos 不存在，使用 contractAttachments 并从后端获取文件名
        const { getFileName } = await import('../api/file')
        contractDetail.contractAttachments = await Promise.all(contractDetail.contractAttachments.map(async (att) => {
          if (typeof att === 'string') {
            // 如果是URL字符串，从后端获取文件名
            let url = att
            if (!url.startsWith('http://') && !url.startsWith('https://')) {
              const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
              url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
            }
            let fileName = url.split('/').pop() || '附件'
            try {
              const fileNameRes = await getFileName(url)
              if (fileNameRes.code === '000000') {
                // 优先使用 data 字段，如果没有则使用 msg 字段
                fileName = fileNameRes.data || fileNameRes.msg || fileName
              }
            } catch (error) {
                console.warn('获取文件名失败:', error)
            }
            return {
              url: url,
              fileName: fileName,
              originalFileName: fileName,
              size: 0
            }
          }
          // 如果已经是对象，确保有 originalFileName
          if (att.originalFileName) {
            return att
          }
          return {
            ...att,
            originalFileName: att.fileName || att.name || '附件'
          }
        }))
      }
      
      // 如果不是草稿状态，重新加载审批记录
      if (contractDetail.contractStatus !== 'draft') {
        await loadApprovalRecords(contractId)
      } else {
        approvalRecords.value = []
      }
    }
  } catch (error) {
    console.error('重新加载合同详情失败:', error)
  } finally {
    detailLoading.value = false
  }
}

// 在详情对话框中撤销合同
const handleWithdrawInDetail = async () => {
  try {
    await ElMessageBox.confirm('确定要撤销该合同吗？撤销后变为草稿状态', '提示', {
      type: 'warning'
    })
    const contractId = contractDetail.contractId
    if (!contractId) {
      ElMessage.error('合同ID不存在')
      return
    }
    const res = await withdrawContract(contractId)
    if (res.code === '000000') {
      ElMessage.success('撤销成功')
      // 自动切换到草稿状态
      queryParams.contractStatus = 'draft'
      // 重新加载详情数据
      await reloadContractDetail()
      // 刷新列表
      loadContractList()
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('撤销合同失败:', error)
      ElMessage.error('撤销合同失败')
    }
  }
}

// 在详情对话框中编辑合同
const handleEditInDetail = () => {
  const contractId = contractDetail.contractId
  if (!contractId) {
    ElMessage.error('合同ID不存在')
    return
  }
  detailDialogVisible.value = false
  router.push({
    path: '/home/salesman-contract-form',
    query: { id: contractId }
  })
}

// 提交审批
const handleSubmitApproval = async () => {
  // 个人用户不需要确认弹窗，直接提交
  if (!isPersonalUser.value) {
    try {
      await ElMessageBox.confirm('确定要提交审批吗？提交后将进入审批流程', '提示', {
        type: 'warning'
      })
    } catch (error) {
      if (error === 'cancel') {
        return
      }
    }
  }
  
  try {
    const contractId = contractDetail.contractId
    if (!contractId) {
      ElMessage.error('合同ID不存在')
      return
    }
    // 调用更新接口，设置 action 为 submit
    const res = await updateContract({
      contractId: contractId,
      customerId: contractDetail.customerId,
      action: 'submit'
    })
    if (res.code === '000000') {
      ElMessage.success('提交审批成功')
      // 个人用户提交审批后，自动切换到已通过状态
      if (isPersonalUser.value) {
        queryParams.contractStatus = 'approved'
      } else {
        // 自动切换到待审批状态
        queryParams.contractStatus = 'pending'
      }
      // 重新加载详情数据
      await reloadContractDetail()
      // 刷新列表
      loadContractList()
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('提交审批失败:', error)
      ElMessage.error('提交审批失败')
    }
  }
}

const handleWithdraw = async (row) => {
  try {
    await ElMessageBox.confirm('确定要撤销该合同吗？撤销后变为草稿状态', '提示', {
      type: 'warning'
    })
    // 使用 contractId 而不是 id
    const contractId = row.contractId || row.id
    if (!contractId) {
      ElMessage.error('合同ID不存在')
      return
    }
    const res = await withdrawContract(contractId)
    if (res.code === '000000') {
      ElMessage.success('撤销成功')
      // 自动切换到草稿状态
      queryParams.contractStatus = 'draft'
      loadContractList()
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('撤销合同失败:', error)
      ElMessage.error('撤销合同失败')
    }
  }
}

const handleSizeChange = (val) => {
  queryParams.pageSize = val
  queryParams.page = 1
  loadContractList()
}

const handlePageChange = (val) => {
  queryParams.page = val
  loadContractList()
}

// 导出合同列表
const handleExport = async () => {
  // 限流检查：如果正在导出，直接返回
  if (exportLoading.value) {
    return
  }

  // 限流检查：如果距离上次导出时间太短，提示用户
  const now = Date.now()
  const timeSinceLastExport = now - lastExportTime.value
  if (lastExportTime.value > 0 && timeSinceLastExport < EXPORT_THROTTLE_INTERVAL) {
    const remainingSeconds = Math.ceil((EXPORT_THROTTLE_INTERVAL - timeSinceLastExport) / 1000)
    ElMessage.warning(`导出操作过于频繁，请等待 ${remainingSeconds} 秒后再试`)
    return
  }
  
  try {
    exportLoading.value = true
    
    // 构建导出参数（不包含分页参数）
    const exportParams = {
      customerId: queryParams.customerId,
      customerName: queryParams.customerName,
      contractStatus: queryParams.contractStatus,
      contractType: queryParams.contractType,
      remark: queryParams.remark,
      startDate: queryParams.startDate,
      endDate: queryParams.endDate
    }
    
    const blob = await exportContractList(exportParams)
    
    if (!blob || blob.size === 0) {
      throw new Error('导出失败：返回数据格式错误')
    }
    
    // 创建下载链接
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `合同列表_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}_${Date.now()}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    ElMessage.success('导出成功')
    // 导出成功后才记录时间戳，失败时不限流
    lastExportTime.value = Date.now()
  } catch (error) {
    console.error('导出失败:', error)
    const errorMsg = error.message || '导出失败'
    // 如果是限流错误（数据量过大），使用warning提示
    if (errorMsg.includes('数据量过大') || errorMsg.includes('超过最大限制')) {
      ElMessage.warning(errorMsg)
    } else {
      ElMessage.error(errorMsg)
    }
    // 导出失败时不更新时间戳，允许用户立即重试
  } finally {
    exportLoading.value = false
  }
}

// 检查并打开详情的公共函数
const checkAndOpenDetail = async () => {
  if (route.query.id) {
    const contractId = route.query.id
    // 直接通过 id 调用接口获取详情
    try {
      await handleView({ contractId: contractId, id: contractId })
      // 清除路由参数，避免刷新时重复触发
      router.replace({ path: route.path, query: {} })
    } catch (error) {
      console.error('获取合同详情失败:', error)
      ElMessage.error('获取合同详情失败')
    }
  }
}

onMounted(async () => {
  // 检查并打开详情
  await checkAndOpenDetail()
  
  // 加载合同列表
  await loadContractList()
})

// 页面激活时（keepAlive 缓存页面）
onActivated(async () => {
  // 如果路由参数中有 refresh 标识，则刷新列表（从表单页返回时需要刷新）
  if (route.query.refresh === 'true') {
    // 如果路由参数中有 contractStatus，自动设置筛选条件
    if (route.query.contractStatus) {
      queryParams.contractStatus = route.query.contractStatus
    }
    await loadContractList()
    // 清除 refresh 和 contractStatus 参数，避免重复刷新和设置
    const newQuery = { ...route.query }
    delete newQuery.refresh
    delete newQuery.contractStatus
    router.replace({ path: route.path, query: newQuery })
  }
  
  // 检查并打开详情
  await checkAndOpenDetail()
})
</script>

<style scoped>
.contract-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* 搜索表单样式，参考商品管理页面的布局 */
.search-form {
  margin-bottom: 20px;
}

.search-form :deep(.el-form-item) {
  margin-bottom: 18px;
}

/* el-row 的 gutter=20 会在左右产生 -10px 的负边距，同时每个 el-col 会有 10px 的左右 padding */
/* 商品管理使用 inline form，没有 gutter，所以第一个表单项直接从 card body 的 padding (20px) 开始 */
/* 为了让合同管理的第一个 col 对齐商品管理，需要移除第一个 col 的左边 padding，同时抵消 el-row 的左边负边距 */
.search-form :deep(.el-row) {
  margin-left: 0 !important; /* 移除 gutter 产生的左边负边距 */
  margin-right: -10px !important; /* 保持右边的负边距，配合最后一个 col 的 padding-right: 0 */
}

/* 第一个 col 左边不添加 padding，让它从 card body 的 padding 开始，与商品管理对齐 */
.search-form :deep(.el-col:first-child) {
  padding-left: 0 !important;
}

/* 最后一个 col 右边不添加 padding，与商品管理对齐 */
.search-form :deep(.el-col:last-child) {
  padding-right: 0 !important;
}

.action-buttons-inline {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

/* 确保表格左侧对齐 */
.contract-management :deep(.el-card__body) {
  padding: 20px;
}

.contract-management :deep(.el-table) {
  margin: 0;
  margin-top: 0;
}

.contract-management :deep(.el-table__header) {
  font-weight: 600;
}

.contract-management :deep(.el-table .el-table__cell) {
  padding: 12px 0;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 合同详情对话框样式 */
.contract-detail-container {
  display: flex;
  gap: 20px;
  padding: 0;
  align-items: flex-start;
}

.contract-detail-left {
  flex: 1;
  min-width: 0;
  max-width: 900px;
}

/* 草稿状态下，左侧区域占满整个宽度 */
.contract-detail-left.full-width {
  max-width: 100%;
}

.contract-detail {
  padding: 0;
}

.contract-detail-right {
  width: 420px;
  flex-shrink: 0;
}

.approval-record-card {
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 250px);
  overflow-y: auto;
}

.approval-record-card :deep(.el-card__body) {
  padding: 20px;
  max-height: calc(100vh - 320px);
  overflow-y: auto;
}

.detail-card {
  margin-bottom: 20px;
}

.detail-card:last-child {
  margin-bottom: 0;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-text {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.title-tip {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}

:deep(.el-descriptions__label) {
  font-weight: 500;
  width: 120px;
}

:deep(.el-descriptions__content) {
  word-break: break-word;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
}

.attachment-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.attachment-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 10px;
  background-color: #f8f9fa;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  transition: all 0.3s;
  width: calc(50% - 6px); /* 每行显示2个，减去gap的一半 */
  box-sizing: border-box;
}

.attachment-item:hover {
  background-color: #f0f2f5;
  border-color: #c0c4cc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.attachment-icon-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-right: 12px;
  flex-shrink: 0;
  border-radius: 6px;
  background-color: #f5f7fa;
  position: relative;
}

.attachment-icon {
  font-size: 24px;
}

.file-type-label {
  position: absolute;
  bottom: 2px;
  left: 0;
  right: 0;
  font-size: 9px;
  font-weight: 600;
  text-align: center;
  line-height: 1;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

/* Excel 图标样式 */
.icon-excel {
  background: linear-gradient(135deg, #1d6f42 0%, #2a8f56 100%);
}

.icon-excel .attachment-icon {
  color: #fff;
}

/* Word 图标样式 */
.icon-word {
  background: linear-gradient(135deg, #2b579a 0%, #3d6fb8 100%);
}

.icon-word .attachment-icon {
  color: #fff;
}

/* PDF 图标样式 */
.icon-pdf {
  background: linear-gradient(135deg, #d04423 0%, #e85a3a 100%);
}

.icon-pdf .attachment-icon {
  color: #fff;
}

/* 文本图标样式 */
.icon-txt {
  background: linear-gradient(135deg, #666666 0%, #808080 100%);
}

.icon-txt .attachment-icon {
  color: #fff;
}

/* 图片图标样式 */
.icon-image {
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
}

.icon-image .attachment-icon {
  color: #fff;
}

/* PPT 图标样式 */
.icon-ppt {
  background: linear-gradient(135deg, #d04423 0%, #e85a3a 100%);
}

.icon-ppt .attachment-icon {
  color: #fff;
}

/* 压缩包图标样式 */
.icon-zip {
  background: linear-gradient(135deg, #ff9800 0%, #ffb340 100%);
}

.icon-zip .attachment-icon {
  color: #fff;
}

/* 默认图标样式 */
.icon-default {
  background: #f5f7fa;
}

.icon-default .attachment-icon {
  color: #909399;
}

.attachment-name {
  flex: 1;
  color: #303133;
  font-size: 14px;
  font-weight: 500;
  word-break: break-all;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 12px;
}

.attachment-size {
  color: #909399;
  font-size: 13px;
  margin-right: 12px;
  flex-shrink: 0;
  min-width: 50px;
}

.attachment-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

/* 图片附件样式 */
.attachment-item-image {
  display: flex;
  align-items: flex-start;
  padding: 14px;
  border: 1px solid #e4e7ed;
  background-color: #ffffff;
  width: calc(50% - 6px); /* 每行显示2个，减去gap的一半 */
  box-sizing: border-box;
}

.attachment-item-image:hover {
  border-color: #c0c4cc;
}

.attachment-image-wrapper {
  position: relative;
  width: 100px;
  height: 100px;
  margin-right: 14px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.attachment-image-wrapper:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transform: scale(1.02);
}

.attachment-image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.attachment-image-preview:hover {
  transform: scale(1.05);
}

.image-slot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 20px;
}

.download-overlay {
  position: absolute;
  bottom: 4px;
  right: 4px;
  opacity: 1;
  z-index: 3;
}

.download-overlay-btn {
  background-color: rgba(64, 158, 255, 0.9);
  border: none;
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.download-overlay-btn:hover {
  background-color: #409eff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.4);
}

.attachment-image-info {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 14px;
}

.attachment-image-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.attachment-image-details .attachment-name {
  font-size: 14px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 0;
}

.attachment-image-details .attachment-size {
  font-size: 12px;
  color: #909399;
  margin-right: 0;
}

/* 对话框底部按钮样式 */
.dialog-footer {
  display: flex;
  justify-content: center;
  align-items: center;
}

.footer-center {
  width: 100%;
  display: flex;
  justify-content: center;
}

.footer-buttons {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  width: 100%;
}

.footer-right {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

/* 审批进度对话框样式 */
.approval-progress-dialog {
  padding: 0;
}

.contract-info {
  margin-bottom: 20px;
}

.approval-timeline-wrapper {
  padding: 10px 0;
}

/* 操作列样式 - 整体右移 */
:deep(.operation-column .cell) {
  padding-left: 20px !important;
}

.operation-buttons {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-start;
}

/* 提交审批按钮淡蓝色样式 */
.submit-approval-btn {
  background-color: #5B9BD5;
  border-color: #5B9BD5;
  color: #fff;
}

.submit-approval-btn:hover {
  background-color: #4A8BC2;
  border-color: #4A8BC2;
}

.submit-approval-btn:disabled {
  background-color: #C0C4CC;
  border-color: #C0C4CC;
  color: #fff;
}
</style>

